import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { marked } from "marked";
import DOMPurify from "dompurify";
import api from "../../../../../chat/api";
import { CalendarIcon, FileIcon, ClientIcon } from "./setupIcons";
import ProposalCopilot from "./ProposalCopilot";
import Tooltip from "../../../Project Page/Tooltip";
import coverageEmptyImg from "../Draft/Rfp-coverage.png";

const HelpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#9CA3AF" strokeWidth="1.6" />
    <path
      d="M9.6 9.4a2.4 2.4 0 1 1 3.3 2.2c-.7.3-1 .8-1 1.6"
      stroke="#9CA3AF"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16.6" r="0.9" fill="#9CA3AF" />
  </svg>
);

const CoverageIllustration = () => (
  <img
    src={coverageEmptyImg}
    alt=""
    aria-hidden="true"
    className="w-[178px] h-auto"
  />
);

// "RFP Coverage" tab — empty until the user runs the analysis (POST /readiness/).
const RfpCoverageContent = ({ folderId }) => {
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    if (!folderId || running) return;
    setRunning(true);
    try {
      await api.post(`api/v1/rfp/folders/${folderId}/readiness/`, {});
      toast.success("RFP analysis started.");
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Couldn't run analysis. Please try again."
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col px-6 py-5">
      <div className="flex items-center gap-2">
        <h4 className="text-[#3F3F46] font-outfit text-[13px] font-semibold tracking-[0.06em]">
          CATEGORY WISE RFP COVERAGE
        </h4>
        <Tooltip
          content="AI assesses scored requirements and highlights coverage gaps. Category bars show progress, while administrative items remain on the Submission Checklist."
          placement="left"
        >
          <HelpIcon />
        </Tooltip>
      </div>
      <p className="text-[#9CA3AF] font-outfit text-[13px] mt-1">
        Click categories to review requirements
      </p>

      <div className="flex-1 flex flex-col items-center justify-center text-center px-2">
        <CoverageIllustration />
        <p className="text-[#18101C] font-outfit text-lg font-semibold mt-4">
          No coverage data yet!
        </p>
        <p className="text-[#6B7280] font-outfit text-sm mt-1 max-w-[300px]">
          Analyze your RFP to see category-wise coverage of requirements
        </p>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={handleRun}
          disabled={running}
          className="w-full py-3 rounded-lg bg-[#9D4E9A] text-white font-outfit text-[15px] font-semibold hover:bg-[#8c3f84] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {running ? "Running…" : "Run Analysis"}
        </button>
      </div>
    </div>
  );
};

// Markdown → sanitized HTML for the brief summary.
const briefToHtml = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  try {
    return DOMPurify.sanitize(marked.parse(s), { USE_PROFILES: { html: true } });
  } catch (err) {
    return DOMPurify.sanitize(`<p>${s}</p>`, { USE_PROFILES: { html: true } });
  }
};

// "2025-08-31" → "Aug 31, 2025" (parsed as local time to avoid a TZ day-shift).
const formatDue = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const BRIEF_MD_STYLES = `
  .rfp-brief-md{color:rgba(0,0,0,0.70);font-size:13px;line-height:20.8px;font-weight:400;}
  .rfp-brief-md p{margin:0 0 10px;}
  .rfp-brief-md strong{font-weight:600;color:#18101C;}
  .rfp-brief-md ul,.rfp-brief-md ol{margin:8px 0 10px;padding-left:20px;}
  .rfp-brief-md ul{list-style:disc;}
  .rfp-brief-md ol{list-style:decimal;}
  .rfp-brief-md li{margin:3px 0;}
  .rfp-brief-md h1,.rfp-brief-md h2,.rfp-brief-md h3,.rfp-brief-md h4{font-weight:600;color:#18101C;margin:12px 0 6px;font-size:14px;}
`;

const RfpBriefContent = ({ data, loading, error, onRetry }) => {
  if (loading) {
    return (
      <div className="flex-1 space-y-3 p-1">
        <div className="h-5 w-2/5 rounded bg-gray-200 animate-pulse" />
        {[96, 90, 98, 82, 92, 65].map((w, i) => (
          <div
            key={i}
            className="h-3 rounded bg-gray-200 animate-pulse"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-16">
        <p className="text-[#18101C] font-outfit text-[15px] font-semibold">
          Couldn't load the RFP brief
        </p>
        <p className="text-[#6B7280] font-outfit text-sm mt-1">
          Something went wrong while generating the summary.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 px-4 py-2 rounded-md border border-[#C95EBE] text-[#C95EBE] font-outfit text-[14px] font-medium hover:bg-purple-50 transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  const docs = data?.document_summaries || [];
  const summaryHtml = briefToHtml(data?.analysis_summary);

  if (!summaryHtml && docs.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-16">
        <p className="text-[#18101C] font-outfit text-[15px] font-semibold">
          No brief available yet
        </p>
        <p className="text-[#6B7280] font-outfit text-sm mt-1 max-w-[280px]">
          Upload and analyze your RFP documents to generate a brief here.
        </p>
      </div>
    );
  }

  const due = formatDue(data?.due_date);
  const client = data?.client;
  const scope = Array.isArray(data?.scope_of_work) ? data.scope_of_work : [];

  return (
    <div className="overflow-y-auto custom-scrollbar px-6 py-5">
      <style>{BRIEF_MD_STYLES}</style>

      <h3 className="text-[#1A1A1A] font-outfit text-[15px] font-semibold leading-[20.15px] mb-1">
        {data?.folder_name || "RFP Brief"}
      </h3>
      <p className="text-[rgba(75,75,75,0.76)] font-outfit text-[12px] font-normal leading-[normal] mb-4">
        Docusensa generated brief · updated just now
      </p>

      {summaryHtml ? (
        <div
          className="rfp-brief-md font-[15px] font-outfit mb-6"
          dangerouslySetInnerHTML={{ __html: summaryHtml }}
        />
      ) : (
        <p className="text-[#9CA3AF] font-outfit text-[14px] italic mb-6">
          No summary was generated for this RFP.
        </p>
      )}

      {(client || due || docs.length > 0) && (
        <>
          <h4 className="text-[#767676] font-outfit text-[12px] font-semibold leading-[normal] uppercase mb-1">
            ABOUT
          </h4>
          <div className="mb-6">
            {client && (
              <div className="flex items-center justify-between gap-3 py-3 border-b border-[#F2F0F4]">
                <span className="flex items-center gap-2 text-[#767676] font-outfit text-[12.5px] font-normal leading-[normal] flex-shrink-0">
                  <ClientIcon /> Client
                </span>
                <span className="text-[#1A1A1A] font-outfit text-[13px] font-medium leading-[normal] text-right truncate">
                  {client}
                </span>
              </div>
            )}
            {due && (
              <div className="flex items-center justify-between py-3 border-b border-[#F2F0F4]">
                <span className="flex items-center gap-2 text-[#767676] font-outfit text-[12.5px] font-normal leading-[normal]">
                  <CalendarIcon /> Due
                </span>
                <span className="bg-[#F3E8F1] text-[#C95EBE] px-3 py-1 rounded-md font-outfit text-[13px] font-medium">
                  {due}
                </span>
              </div>
            )}
            {docs.length > 0 && (
              <div className="flex items-center justify-between gap-3 py-3 border-b border-[#F2F0F4]">
                <span className="flex items-center gap-2 text-[#767676] font-outfit text-[12.5px] font-normal leading-[normal] flex-shrink-0">
                  <FileIcon /> Source
                </span>
                <span className="text-[#1A1A1A] font-outfit text-[13px] font-medium leading-[normal] text-right truncate">
                  {docs.length === 1
                    ? docs[0].name
                    : `${docs.length} documents`}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {scope.length > 0 && (
        <>
          <h4 className="text-[#5C5C5C] font-outfit text-[12px] font-semibold leading-[normal] uppercase mb-3">
            SCOPE OF WORK
          </h4>
          <ol className="list-decimal pl-5 space-y-2 mb-6 marker:text-[#000000B3] marker:font-medium">
            {scope.map((item, i) => (
              <li
                key={i}
                className="text-[#000000B3] font-outfit text-[13px] font-medium leading-[normal] pl-1"
              >
                {item}
              </li>
            ))}
          </ol>
        </>
      )}

      {docs.length > 0 && (
        <>
          <h4 className="text-[#9CA3AF] font-outfit text-[12px] font-semibold tracking-[0.08em] mb-3">
            SOURCE DOCUMENTS
          </h4>
          <ul className="space-y-2.5">
            {docs.map((d) => (
              <li key={d.id} className="flex items-start gap-2">
                <span className="mt-0.5 flex-shrink-0">
                  <FileIcon />
                </span>
                <span className="text-[#18101C] font-outfit text-[14px] leading-5 break-words">
                  {d.name}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, disabled = false, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`relative pb-2 font-outfit text-[14px] leading-[normal] text-center ${
      disabled
        ? "text-[#C9C9D0] font-medium cursor-not-allowed"
        : active
        ? "text-[#A04A97] font-semibold"
        : "text-[#767676] font-medium"
    }`}
  >
    {children}
    {active && !disabled && (
      <span className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-[#C95EBE]" />
    )}
  </button>
);

const ProposalCopilotPanel = ({
  tab = "brief",
  setTab = () => {},
  onClose = () => {},
  copilotMessages,
  setCopilotMessages,
  copilotChatId,
  setCopilotChatId,
  // Draft-step wiring: the section the editor is on (sent with each message) and
  // a refresh callback to pull the copilot's edits back into the editor.
  activeSectionId = null,
  activeSectionIsSubsection = false,
  onRefreshProposal,
  onDraftStreaming,
  showCoverage = false,
  folderId = null,
  noTopBorder = false,
  // Setup step keeps the panel permanently open, so it hides the close control.
  hideClose = false,
}) => {
  // Fall back to Copilot if the coverage tab is removed while active.
  useEffect(() => {
    if (!showCoverage && tab === "coverage") setTab("copilot");
  }, [showCoverage, tab, setTab]);

  // RFP Brief is fetched lazily on first open (the POST may trigger synchronous
  // summary generation), then kept across tab switches.
  const [briefData, setBriefData] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState(false);
  const briefLoadedRef = useRef(false);

  const loadBrief = useCallback(async () => {
    if (!folderId) return;
    briefLoadedRef.current = true;
    setBriefLoading(true);
    setBriefError(false);
    try {
      const res = await api.post(
        `api/v1/rfp/folders/${folderId}/analysis-summary/`,
        {}
      );
      setBriefData(res.data);
    } catch (error) {
      briefLoadedRef.current = false; // allow a retry
      setBriefError(true);
    } finally {
      setBriefLoading(false);
    }
  }, [folderId]);

  useEffect(() => {
    if (tab === "brief" && folderId && !briefLoadedRef.current) loadBrief();
  }, [tab, folderId, loadBrief]);

  return (
    <aside className={`w-[380px] flex-shrink-0 border-b border-[#ECE9EE] bg-white flex flex-col shadow-[-14px_0_28px_-22px_rgba(42,10,34,0.30)] ${noTopBorder ? "" : "border-t"}`}>
      <div className="flex items-end justify-between px-6 pt-2 border-b border-[#ECE9EE]">
        <div className="flex items-center gap-6 mt-1">
          <TabButton active={tab === "copilot"} onClick={() => setTab("copilot")}>
            Copilot
          </TabButton>
          {showCoverage && (
            <TabButton
              active={tab === "coverage"}
              onClick={() => setTab("coverage")}
              disabled
            >
              RFP Coverage
            </TabButton>
          )}
          <TabButton active={tab === "brief"} onClick={() => setTab("brief")}>
            RFP Brief
          </TabButton>
        </div>
        {!hideClose && (
          <button
            onClick={onClose}
            className="text-[#9CA3AF] hover:text-[#18101C] pb-2"
            aria-label="Close panel"
          >
            ✕
          </button>
        )}
      </div>

      {tab === "brief" && (
        <RfpBriefContent
          data={briefData}
          loading={briefLoading}
          error={briefError}
          onRetry={loadBrief}
        />
      )}
      {tab === "coverage" && showCoverage && (
        <RfpCoverageContent folderId={folderId} />
      )}
      {/* Keep Copilot mounted (hidden) so its chat session persists across tab switches. */}
      <div className={tab === "copilot" ? "flex-1 min-h-0 flex flex-col" : "hidden"}>
        <ProposalCopilot
          isActive={tab === "copilot"}
          folderId={folderId}
          messages={copilotMessages}
          setMessages={setCopilotMessages}
          chatId={copilotChatId}
          setChatId={setCopilotChatId}
          activeSectionId={activeSectionId}
          activeSectionIsSubsection={activeSectionIsSubsection}
          onRefreshProposal={onRefreshProposal}
          onDraftStreaming={onDraftStreaming}
        />
      </div>
    </aside>
  );
};

export default ProposalCopilotPanel;
